import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api, asList, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Copy, GripVertical, MapPin, Check } from "lucide-react";

// Excel-achtige inline tabel: alle velden direct bewerkbaar, opslaan bij blur.

const NEW_PRODUCT = {
    sku: "",
    name: "",
    cultivar: "",
    pot_size: "",
    pot_liters: "",
    units_per_layer: 12,
    gap_units: 4,
    max_layers_per_cart: 0,
    location_ids: [],
    mix_allowed: true,
    mix_within_layer_allowed: false,
    active: true,
};

export default function Products() {
    const [products, setProducts] = useState([]);
    const [locations, setLocations] = useState([]);
    const [savingIds, setSavingIds] = useState(new Set());
    const [savedIds, setSavedIds] = useState(new Set());
    const [dragId, setDragId] = useState(null);
    const [reorderBusy, setReorderBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const [loadError, setLoadError] = useState("");
    const latestProducts = useRef([]);
    const saveQueues = useRef(new Map());
    const saveRevisions = useRef(new Map());

    const load = async () => {
        setLoadError("");
        try {
            const [p, locs] = await Promise.all([
                api.get("/products"),
                api.get("/locations"),
            ]);
            const nextProducts = asList(p.data, ["products"]);
            latestProducts.current = nextProducts;
            setProducts(nextProducts);
            setLocations(asList(locs.data, ["locations"]));
        } catch (e) {
            setLoadError(formatApiErrorDetail(e.response?.data?.detail ?? "Producten konden niet worden geladen"));
        }
    };
    useEffect(() => {
        load();
    }, []);

    // Patch één product met nieuwe velden, optimistisch in state én PUT naar server.
    const saveField = async (product, changes) => {
        const current = latestProducts.current.find((item) => item.id === product.id) || product;
        const merged = { ...current, ...changes };
        // Optimistisch in lijst
        latestProducts.current = latestProducts.current.map((p) => (p.id === product.id ? merged : p));
        setProducts(latestProducts.current);
        // Skip server-call als nieuwe product nog geen SKU/naam heeft
        if (!merged.sku?.trim() || !merged.name?.trim()) return;

        const revision = (saveRevisions.current.get(product.id) || 0) + 1;
        saveRevisions.current.set(product.id, revision);
        setSavingIds((s) => new Set(s).add(product.id));
        const previous = saveQueues.current.get(product.id) || Promise.resolve();
        const request = previous.catch(() => {}).then(async () => {
            const { id: _ignore, ...payload } = merged;
            return api.put(`/products/${product.id}`, payload);
        });
        saveQueues.current.set(product.id, request);
        try {
            const { data } = await request;
            if (saveRevisions.current.get(product.id) === revision) {
                latestProducts.current = latestProducts.current.map((p) => (p.id === product.id ? data : p));
                setProducts(latestProducts.current);
                setSavedIds((s) => new Set(s).add(product.id));
                setTimeout(() => {
                    setSavedIds((s) => {
                        const n = new Set(s);
                        n.delete(product.id);
                        return n;
                    });
                }, 1500);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail ?? "Opslaan mislukt"));
            if (saveRevisions.current.get(product.id) === revision) await load();
        } finally {
            if (saveQueues.current.get(product.id) === request) saveQueues.current.delete(product.id);
            if (saveRevisions.current.get(product.id) === revision) {
                setSavingIds((s) => {
                    const n = new Set(s);
                    n.delete(product.id);
                    return n;
                });
            }
        }
    };

    const addRow = async () => {
        if (creating) return;
        setCreating(true);
        try {
            const sku = `NIEUW-${Date.now().toString(36).toUpperCase().slice(-5)}`;
            const payload = { ...NEW_PRODUCT, sku, name: "Nieuw product" };
            const { data } = await api.post("/products", payload);
            latestProducts.current = [data, ...latestProducts.current];
            setProducts(latestProducts.current);
            // Focus de SKU-cel van de nieuwe rij
            setTimeout(() => {
                const el = document.querySelector(`[data-cell="${data.id}-sku"]`);
                if (el) {
                    el.focus();
                    el.select?.();
                }
            }, 80);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail ?? "Aanmaken mislukt"));
        } finally {
            setCreating(false);
        }
    };

    const duplicate = async (p) => {
        try {
            const { id: _id, ...rest } = p;
            const { data } = await api.post("/products", { ...rest, sku: `${p.sku}-KOPIE` });
            latestProducts.current = [data, ...latestProducts.current];
            setProducts(latestProducts.current);
            toast.success("Gedupliceerd");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail ?? "Dupliceren mislukt"));
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Product ${p.sku} verwijderen?`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            latestProducts.current = latestProducts.current.filter((x) => x.id !== p.id);
            setProducts(latestProducts.current);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail ?? "Verwijderen mislukt"));
        }
    };

    // Drag-reorder (laadvolgorde)
    const onDragStart = (id) => (e) => {
        setDragId(id);
        e.dataTransfer.effectAllowed = "move";
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };
    const onDrop = (targetId) => async (e) => {
        e.preventDefault();
        if (!dragId || dragId === targetId) return setDragId(null);
        const list = [...products];
        const f = list.findIndex((p) => p.id === dragId);
        const t = list.findIndex((p) => p.id === targetId);
        if (f < 0 || t < 0) return setDragId(null);
        const [moved] = list.splice(f, 1);
        list.splice(t, 0, moved);
        const reordered = list.map((product, index) => ({
            ...product,
            sort_order: (index + 1) * 10,
        }));
        latestProducts.current = reordered;
        setProducts(reordered);
        setDragId(null);
        setReorderBusy(true);
        try {
            // Laat reeds gestarte veldwijzigingen eerst afronden; de reorder-call
            // schrijft daarna als laatste de definitieve sort_order naar de server.
            await Promise.allSettled(Array.from(saveQueues.current.values()));
            const currentOrder = latestProducts.current.map((product, index) => ({
                ...product,
                sort_order: (index + 1) * 10,
            }));
            latestProducts.current = currentOrder;
            setProducts(currentOrder);
            await api.post("/products/reorder", { product_ids: currentOrder.map((p) => p.id) });
            toast.success("Volgorde opgeslagen");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail ?? "Volgorde opslaan mislukt"));
            await load();
        } finally {
            setReorderBusy(false);
        }
    };

    return (
        <div data-testid="products-page">
            <PageHeader
                kicker="Beheer"
                title="Producten"
                subtitle={`${products.length} producten · klik in een cel om te bewerken, wijzigingen worden direct opgeslagen.`}
                testid="products-header"
                actions={
                    <button
                        onClick={addRow}
                        disabled={creating}
                        data-testid="btn-add-product"
                        className="px-3 py-1.5 rounded-md bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                        <Plus className="h-4 w-4" /> Nieuw product
                    </button>
                }
            />

            {loadError && (
                <div className="mb-5 flex items-center justify-between gap-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    <span>{loadError}</span>
                    <button type="button" onClick={load} className="font-semibold underline">Opnieuw proberen</button>
                </div>
            )}

            <div className="bg-white border border-border rounded-md overflow-hidden" data-testid="products-list">
                <div className="px-4 py-2 border-b border-border bg-ink-50/40 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 text-ink-500">
                        <GripVertical className="h-3.5 w-3.5 text-ink-400" />
                        <span>
                            <span className="font-mono uppercase tracking-wider text-[10px] text-ink-500">Laadvolgorde:</span>{" "}
                            sleep rijen om de pakvolgorde aan te passen
                        </span>
                    </div>
                    {reorderBusy && (
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-500">opslaan…</div>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="products-table">
                        <thead>
                            <tr className="bg-ink-50 border-b border-border">
                                <th className="w-8 px-2 py-2"></th>
                                <th className="text-left px-3 py-2 data-label">SKU</th>
                                <th className="text-left px-3 py-2 data-label">Naam</th>
                                <th className="text-left px-3 py-2 data-label">Cultivar</th>
                                <th className="text-left px-3 py-2 data-label">Pot</th>
                                <th className="text-left px-3 py-2 data-label">Liter</th>
                                <th className="text-right px-3 py-2 data-label">Stuks/Laag</th>
                                <th className="text-right px-3 py-2 data-label">Tussengaten</th>
                                <th className="text-left px-3 py-2 data-label">Locaties</th>
                                <th className="w-20 px-2 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-5 py-10 text-center text-ink-500">
                                        Nog geen producten — klik op &quot;Nieuw product&quot; om te beginnen.
                                    </td>
                                </tr>
                            )}
                            {products.map((p) => (
                                <ProductRow
                                    key={p.id}
                                    product={p}
                                    locations={locations}
                                    isDragged={dragId === p.id}
                                    isSaving={savingIds.has(p.id)}
                                    isSaved={savedIds.has(p.id)}
                                    onDragStart={onDragStart}
                                    onDragOver={onDragOver}
                                    onDrop={onDrop}
                                    onSave={(changes) => saveField(p, changes)}
                                    onDuplicate={() => duplicate(p)}
                                    onRemove={() => remove(p)}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function ProductRow({
    product: p,
    locations,
    isDragged,
    isSaving,
    isSaved,
    onDragStart,
    onDragOver,
    onDrop,
    onSave,
    onDuplicate,
    onRemove,
}) {
    return (
        <tr
            draggable
            onDragStart={onDragStart(p.id)}
            onDragOver={onDragOver}
            onDrop={onDrop(p.id)}
            className={`border-t border-border/60 hover:bg-ink-50/30 ${isDragged ? "opacity-40" : ""} ${isSaved ? "bg-florefy-50/40" : ""}`}
            data-testid={`product-row-${p.sku}`}
        >
            <td className="px-2 py-1 text-center cursor-grab active:cursor-grabbing align-middle" aria-label="Sleep om volgorde aan te passen">
                <GripVertical className="h-4 w-4 text-ink-300 hover:text-ink-700 mx-auto" />
            </td>
            <td className="px-1 py-0.5">
                <EditableText value={p.sku} onSave={(v) => onSave({ sku: v })} mono testid={`cell-${p.id}-sku`} />
            </td>
            <td className="px-1 py-0.5">
                <EditableText value={p.name} onSave={(v) => onSave({ name: v })} testid={`cell-${p.id}-name`} />
            </td>
            <td className="px-1 py-0.5">
                <EditableText value={p.cultivar} onSave={(v) => onSave({ cultivar: v })} italic testid={`cell-${p.id}-cultivar`} placeholder="—" />
            </td>
            <td className="px-1 py-0.5">
                <EditableText value={p.pot_size} onSave={(v) => onSave({ pot_size: v })} mono w="w-16" testid={`cell-${p.id}-pot`} placeholder="—" />
            </td>
            <td className="px-1 py-0.5">
                <EditableText value={p.pot_liters} onSave={(v) => onSave({ pot_liters: v })} mono w="w-16" testid={`cell-${p.id}-liters`} placeholder="—" />
            </td>
            <td className="px-1 py-0.5 text-right">
                <EditableNumber value={p.units_per_layer} onSave={(v) => onSave({ units_per_layer: v })} testid={`cell-${p.id}-upl`} />
            </td>
            <td className="px-1 py-0.5 text-right">
                <EditableNumber value={p.gap_units} onSave={(v) => onSave({ gap_units: v })} testid={`cell-${p.id}-gap`} />
            </td>
            <td className="px-1 py-0.5">
                <LocationPicker
                    value={p.location_ids || []}
                    options={locations}
                    onSave={(v) => onSave({ location_ids: v })}
                    testid={`cell-${p.id}-locs`}
                />
            </td>
            <td className="px-2 py-1 text-right">
                <div className="flex items-center justify-end gap-1">
                    {isSaving && (
                        <span className="text-[9px] font-mono uppercase text-ink-400" data-testid={`saving-${p.id}`}>
                            …
                        </span>
                    )}
                    {isSaved && !isSaving && (
                        <Check className="h-3.5 w-3.5 text-florefy-700" data-testid={`saved-${p.id}`} />
                    )}
                    <button
                        onClick={onDuplicate}
                        title="Dupliceer dit product"
                        data-testid={`btn-dup-${p.sku}`}
                        className="p-1 text-ink-500 hover:text-florefy-900 rounded hover:bg-ink-100"
                    >
                        <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                        onClick={onRemove}
                        title="Verwijder dit product"
                        data-testid={`btn-del-${p.sku}`}
                        className="p-1 text-ink-500 hover:text-destructive rounded hover:bg-destructive/10"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </td>
        </tr>
    );
}

function EditableText({ value, onSave, mono, italic, placeholder, w, testid }) {
    const [val, setVal] = useState(value ?? "");
    useEffect(() => setVal(value ?? ""), [value]);
    const baseCls = `${w || "w-full"} bg-transparent border border-transparent hover:border-ink-200 focus:border-florefy-500 focus:bg-white rounded px-1.5 py-1 text-sm outline-none ${
        mono ? "font-mono text-xs" : ""
    } ${italic ? "italic" : ""}`;
    return (
        <input
            type="text"
            value={val}
            placeholder={placeholder || ""}
            onChange={(e) => setVal(e.target.value)}
            onBlur={() => {
                if ((val ?? "") !== (value ?? "")) onSave(val);
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
                if (e.key === "Escape") {
                    setVal(value ?? "");
                    e.target.blur();
                }
            }}
            data-cell={testid}
            data-testid={testid}
            className={baseCls}
        />
    );
}

function EditableNumber({ value, onSave, placeholder, testid }) {
    const [val, setVal] = useState(String(value ?? 0));
    useEffect(() => setVal(String(value ?? 0)), [value]);
    return (
        <input
            type="number"
            value={val}
            placeholder={placeholder || ""}
            onChange={(e) => setVal(e.target.value)}
            onBlur={() => {
                const n = Number(val);
                if (!Number.isNaN(n) && n !== Number(value)) onSave(n);
                else setVal(String(value ?? 0));
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
                if (e.key === "Escape") {
                    setVal(String(value ?? 0));
                    e.target.blur();
                }
            }}
            data-cell={testid}
            data-testid={testid}
            className="w-20 bg-transparent border border-transparent hover:border-ink-200 focus:border-florefy-500 focus:bg-white rounded px-1.5 py-1 text-sm font-mono text-right outline-none"
        />
    );
}

function LocationPicker({ value, options, onSave, testid }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const ref = useRef(null);
    const popRef = useRef(null);

    // Bereken positie t.o.v. trigger op viewport (fixed) — werkt over scroll-containers heen.
    const updatePos = () => {
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        const popW = 256; // w-64
        let left = r.left;
        if (left + popW > window.innerWidth - 8) left = window.innerWidth - popW - 8;
        setPos({ top: r.bottom + 4, left });
    };
    useEffect(() => {
        if (!open) return;
        updatePos();
        const onScrollOrResize = () => updatePos();
        const onClick = (e) => {
            if (ref.current?.contains(e.target)) return;
            if (popRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        window.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize);
        document.addEventListener("mousedown", onClick);
        return () => {
            window.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize);
            document.removeEventListener("mousedown", onClick);
        };
    }, [open]);

    const selected = options.filter((o) => value.includes(o.id));
    const toggle = (id) => {
        const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
        onSave(next);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid={testid}
                className="w-full text-left px-1.5 py-1 rounded border border-transparent hover:border-ink-200 focus:border-florefy-500 focus:bg-white text-xs flex items-center gap-1 flex-wrap min-h-[28px]"
            >
                {selected.length === 0 && (
                    <span className="text-ink-400 text-xs flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> kies…
                    </span>
                )}
                {selected.slice(0, 3).map((s) => (
                    <span
                        key={s.id}
                        className="inline-block px-1.5 py-0.5 bg-florefy-100 text-florefy-900 rounded-sm font-mono text-[10px] uppercase"
                    >
                        {s.code}
                    </span>
                ))}
                {selected.length > 3 && (
                    <span className="font-mono text-[10px] text-ink-500">+{selected.length - 3}</span>
                )}
            </button>
            {open && createPortal(
                <div
                    ref={popRef}
                    style={{ position: "fixed", top: pos.top, left: pos.left, width: 256, zIndex: 1000 }}
                    className="bg-white border border-border rounded-md shadow-lg p-2 max-h-64 overflow-auto"
                    data-testid={`${testid}-popover`}
                >
                    {options.length === 0 && (
                        <div className="text-xs text-ink-500 px-2 py-2">Geen locaties — voeg toe in /beheer/locaties</div>
                    )}
                    {options.map((o) => {
                        const on = value.includes(o.id);
                        return (
                            <label
                                key={o.id}
                                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-ink-50 cursor-pointer text-sm"
                            >
                                <input
                                    type="checkbox"
                                    checked={on}
                                    onChange={() => toggle(o.id)}
                                    data-testid={`${testid}-opt-${o.code}`}
                                />
                                <span className="font-mono text-xs">{o.code}</span>
                                <span className="text-ink-700 truncate">{o.name}</span>
                            </label>
                        );
                    })}
                </div>,
                document.body
            )}
        </div>
    );
}
